import {
  Step as ModelStep,
  Payer,
  UncoveredPayer,
} from "@redotech/redo-model/return-flow";
import {
  ReturnOptionMethod,
  StoreCreditType,
} from "@redotech/redo-model/return-flow/return-option";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import RefreshIcon from "@redotech/redo-web/icon-old/refresh.svg";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";
import { getModelReturnOptionMethod } from "./return";
import { returnOptionLabels } from "./submit-claim";

export interface SubmitWarrantyOption {
  method: ReturnOptionMethod["type"];
  creditType: StoreCreditType;
  requireSendBack: boolean;
  labelPayer: UncoveredPayer;
  adjustmentFlat: string | undefined;
  adjustmentPercent: string | undefined;
}

export interface SubmitWarrantyState {
  option: SubmitWarrantyOption;
  customTitle?: string;
}

function round(number: number) {
  return Math.round(number * 100) / 100;
}

export const SUBMIT_WARRANTY = (
  Details: React.ComponentType<StepTypeDetailsProps<SubmitWarrantyState>>,
): StepType<SubmitWarrantyState, ModelStep.SubmitWarranty> => ({
  Details: (props: StepTypeDetailsProps<SubmitWarrantyState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: "Submit Warranty",
  valid(state) {
    return (
      !!state.option.method &&
      !isNaN(parseFloat(state.option.adjustmentFlat ?? "0")) &&
      !isNaN(parseFloat(state.option.adjustmentPercent ?? "0"))
    );
  },
  Icon: RefreshIcon,
  description(state) {
    return returnOptionLabels(state.option.method) || "";
  },
  downstream() {
    return [];
  },
  maxDownstream() {
    return 0;
  },
  empty: {
    option: {
      method: ReturnOptionMethod.EXCHANGE,
      creditType: StoreCreditType.GIFT_CARD,
      requireSendBack: true,
      labelPayer: Payer.MERCHANT,
      adjustmentFlat: "0",
      adjustmentPercent: "0",
    },
  },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state) {
    return state;
  },
  toModel(state) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.SUBMIT_WARRANTY,
      option: {
        method: getModelReturnOptionMethod(
          state.option.method,
          state.option.creditType,
        ),
        adjustment: {
          flat: parseFloat(state.option.adjustmentFlat ?? "0"),
          proportion:
            parseFloat(state.option.adjustmentPercent ?? "0") / 100 + 1,
        },
        requireSendBack: state.option.requireSendBack,
        labelPayer: state.option.labelPayer,
      },
    };
  },
  fromModel(model) {
    return {
      customTitle: model.customTitle,
      option: {
        method: model.option.method.type,
        creditType:
          "creditType" in model.option.method
            ? model.option.method.creditType
            : StoreCreditType.GIFT_CARD,
        requireSendBack: model.option.requireSendBack,
        labelPayer: model.option.labelPayer ?? Payer.MERCHANT,
        adjustmentFlat: model.option.adjustment?.flat.toString(),
        adjustmentPercent: round(
          ((model.option.adjustment?.proportion ?? 1) - 1) * 100,
        ).toString(),
      },
    };
  },
});
